// CFG.PUSH.A1 + agent-pause feature: banner that asks the user to opt into
// desktop push, then immediately follows up with an agent-pause debounce
// preference picker (30s vs 60s). The second step is where we persist a
// WEB_PUSH row in notification_preferences — once saved the banner hides.
//
// The banner is deliberately non-blocking: it appears at the top of the
// dashboard only when
//   (a) the browser supports the Web Push API,
//   (b) the user has not already granted or denied permission, and
//   (c) the user has not dismissed the banner on this browser.
//
// "Maybe later" writes a flag to localStorage so the banner stays dismissed
// across reloads on the same device. "Enable" kicks off the full subscribe
// flow in pushSubscribe.ts (must run inside the click handler — Chrome and
// Safari only allow Notification.requestPermission() from a user gesture).

import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  isPushSupported,
  getNotificationPermission,
  enablePushNotifications,
} from '../lib/pushSubscribe';
import { apiFetch } from '../lib/api';

const DISMISS_KEY = 'bp_push_banner_dismissed';

type Step = 'enable' | 'debounce';
type Debounce = 30 | 60;

export default function PushPermissionBanner() {
  const { token } = useAuth();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState<Step>('enable');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [debounce, setDebounce] = useState<Debounce>(30);

  useEffect(() => {
    if (!isPushSupported()) return;
    if (getNotificationPermission() !== 'default') return;

    try {
      if (localStorage.getItem(DISMISS_KEY) === '1') return;
    } catch {
      // localStorage can throw in private-browsing / SSR — fall through and
      // show the banner; the worst case is it reappears on every visit.
    }

    setVisible(true);
  }, []);

  if (!visible) return null;

  const handleEnable = async () => {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      const ok = await enablePushNotifications(token);
      if (ok) {
        // Don't dismiss yet — advance to the debounce step so the user picks
        // how long to wait before we ping them when an agent pauses. The
        // banner stays visible until they click Save.
        setStep('debounce');
      } else {
        // Three common reasons: user denied, VAPID not configured (503), or
        // the push service refused. All three resolve to "try again later".
        setError('Could not enable notifications. Check your browser permissions and try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleSaveDebounce = async () => {
    setBusy(true);
    setError(null);
    try {
      // Persist a WEB_PUSH row with the chosen debounce. Note: enabled=true
      // is implicit via the API default, and pushDebounceSeconds is validated
      // server-side as 30 or 60.
      await apiFetch('/notifications/preferences', {
        method: 'PATCH',
        body: JSON.stringify({
          channel: 'WEB_PUSH',
          enabled: true,
          pushDebounceSeconds: debounce,
        }),
      });
      setVisible(false);
    } catch {
      // Non-fatal: the subscription is already stored, this is just the
      // debounce preference. Let the user retry. Don't strand them in the
      // banner because a non-essential PATCH failed — they can still pick a
      // value in Settings later.
      setError('Could not save your preference. You can adjust this anytime in Settings.');
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore — banner will reappear, which is acceptable */
    }
    setVisible(false);
  };

  return (
    <div
      data-testid="push-permission-banner"
      role="region"
      aria-label="Enable desktop notifications"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        padding: '12px 16px',
        marginBottom: 16,
        borderRadius: 8,
        border: '1px solid var(--border)',
        background: 'var(--surface)',
        borderLeft: '3px solid #7c3aed',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        {step === 'enable' ? (
          <>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>
              Enable desktop notifications
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary, #6b7280)' }}>
              Get alerted when agent sessions complete, gates block, or approvals are needed — even when this tab is in the background.
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>
              Notifications enabled. When should we ping you on agent pauses?
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary, #6b7280)', marginBottom: 8 }}>
              If you don’t reply for a few seconds after an agent pauses, we’ll send a desktop push so you don’t miss it.
            </div>
            <div
              role="radiogroup"
              aria-label="Debounce delay"
              data-testid="push-debounce-options"
              style={{ display: 'flex', gap: 8 }}
            >
              {[30, 60].map(sec => {
                const active = debounce === sec;
                return (
                  <button
                    key={sec}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    data-testid={`push-debounce-${sec}`}
                    onClick={() => setDebounce(sec as Debounce)}
                    disabled={busy}
                    style={{
                      padding: '5px 14px',
                      borderRadius: 6,
                      border: `1.5px solid ${active ? '#7c3aed' : 'var(--border)'}`,
                      background: active ? 'rgba(124,58,237,0.08)' : 'transparent',
                      color: active ? '#7c3aed' : 'var(--text-secondary, #6b7280)',
                      fontSize: 13,
                      fontWeight: active ? 600 : 500,
                      cursor: busy ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {sec}s
                  </button>
                );
              })}
            </div>
          </>
        )}
        {error && (
          <div role="alert" style={{ fontSize: 12, color: '#ef4444', marginTop: 4 }}>
            {error}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        <button
          type="button"
          onClick={handleDismiss}
          disabled={busy}
          style={{
            padding: '6px 12px',
            borderRadius: 6,
            border: '1px solid var(--border)',
            background: 'transparent',
            color: 'var(--text-secondary, #6b7280)',
            fontSize: 13,
            cursor: busy ? 'not-allowed' : 'pointer',
          }}
        >
          {step === 'enable' ? 'Maybe later' : 'Skip'}
        </button>
        {step === 'enable' ? (
          <button
            type="button"
            onClick={handleEnable}
            disabled={busy}
            style={{
              padding: '6px 14px',
              borderRadius: 6,
              border: 'none',
              background: '#7c3aed',
              color: '#fff',
              fontSize: 13,
              fontWeight: 500,
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            {busy ? 'Enabling…' : 'Enable'}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSaveDebounce}
            disabled={busy}
            style={{
              padding: '6px 14px',
              borderRadius: 6,
              border: 'none',
              background: '#7c3aed',
              color: '#fff',
              fontSize: 13,
              fontWeight: 500,
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        )}
      </div>
    </div>
  );
}
